import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  SxProps,
  Theme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { PageHint, IDocKey } from './PageHint';

interface BaseDialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  /**
   * Ключ з `ModalIndices` (`common/i-doc/i-doc.registry`). Якщо передано — на Dialog
   * додається `data-i-doc` атрибут і автоматично рендериться `<PageHint iDoc={iDoc} />`
   * під заголовком. Без необхідності вписувати у кожній модалці окремо.
   */
  iDoc?: IDocKey;
  /**
   * Override `sx` для `DialogContent`. Корисно для модалок з власним внутрішнім
   * padding'ом (edge-to-edge секції, плашки на всю ширину тощо). За замовч. — `{ p: 2 }`.
   */
  contentSx?: SxProps<Theme>;
  /** На малих екранах розгортає модалку на весь екран. */
  fullScreen?: boolean;
  /**
   * Контент над заголовком (наприклад обкладинка поста через `CardMedia`).
   * Рендериться edge-to-edge, до `DialogTitle`.
   */
  headerMedia?: React.ReactNode;
  /** Точкові override стилів під макет: Paper, шапка (DialogTitle), текст заголовка, футер. */
  paperSx?: SxProps<Theme>;
  headerSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
}

/**
 * Універсальний компонент модального вікна з вбудованим хрестиком закриття.
 *
 * Кнопку "Закрити" у footer більше дублювати НЕ треба — користувач закриває хрестиком
 * або кліком поза модалкою. У `actions` передавати лише primary-actions
 * (Save, Submit, Confirm, "Перейти" тощо).
 */
export const BaseDialog: React.FC<BaseDialogProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'sm',
  iDoc,
  contentSx,
  fullScreen,
  headerMedia,
  paperSx,
  headerSx,
  titleSx,
  actionsSx,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fullScreen}
      PaperProps={paperSx ? { sx: paperSx } : undefined}
      {...(iDoc ? { 'data-i-doc': iDoc } : {})}
    >
      {headerMedia}
      <DialogTitle sx={headerSx ?? { p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={titleSx}>{title}</Typography>
          <IconButton onClick={onClose} sx={{ ml: 2 }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      {iDoc && <PageHint iDoc={iDoc} />}
      <DialogContent dividers sx={contentSx ?? { p: 2 }}>
        {children}
      </DialogContent>
      {actions && (
        <DialogActions sx={actionsSx ?? { p: 2 }}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};
